import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { BrandMark } from '../components/BrandMark';
import { ChatWidget } from '../chat/ChatWidget';
import { ConsentBanner } from '../components/ConsentBanner';
import { DemoForm } from '../components/DemoForm';
import { LangSwitcher, LANGS } from '../components/LangSwitcher';
import { PhoneMenu, KdsBoard, LoyaltyCard, FEATURE_ICONS as ICONS } from '../components/MockVisuals';
import { applySeo, LOCALE_PATHS, PARTNER_PATHS, GUIDE_PATHS } from '../lib/seo';
import { startAnalytics, pageview, track } from '../lib/analytics';

interface Pair { title: string; desc: string }
interface Qa { q: string; a: string }

export function MarketingPage({ lang }: { lang: string }) {
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (i18n.resolvedLanguage !== lang) void i18n.changeLanguage(lang);
  }, [lang, i18n]);

  useEffect(() => {
    applySeo({ lang, title: t('marketing.seo_title'), description: t('marketing.seo_description') });
    startAnalytics();
    pageview(LOCALE_PATHS[lang] ?? '/', t('marketing.seo_title'));
  }, [lang, t]);

  const features = t('marketing.features', { returnObjects: true }) as Pair[];
  const steps = t('marketing.steps', { returnObjects: true }) as Pair[];
  const planFeatures = t('marketing.plan_features', { returnObjects: true }) as string[];
  const faq = t('marketing.faq', { returnObjects: true }) as Qa[];

  return (
    <div className="mkt">
      <header className="mkt-header">
        <a className="brand" href={LOCALE_PATHS[lang] ?? '/'}>
          <BrandMark className="brand__mark" />
          <span className="brand__name">La <em>petite</em> <span className="brand__accent">table</span></span>
        </a>
        <nav className="mkt-nav">
          <Link to={GUIDE_PATHS[lang]}>{t('marketing.nav.features')}</Link>
          <a href="#how">{t('marketing.nav.how')}</a>
          <a href="#pricing">{t('marketing.nav.pricing')}</a>
          <a href="#faq">{t('marketing.nav.faq')}</a>
          <Link to={PARTNER_PATHS[lang]}>{t('partners.nav_label')}</Link>
        </nav>
        <div className="mkt-header__right">
          <LangSwitcher lang={lang} paths={LOCALE_PATHS} />
          <Link to="/login" className="mkt-login">{t('marketing.nav.login')}</Link>
          <a href="#demo" className="btn btn--primary btn--sm" onClick={() => track('demo_click', { from: 'nav' })}>{t('demo.title')}</a>
          <button
            type="button"
            className="mkt-burger"
            aria-label="Menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              {menuOpen ? <path d="M6 6l12 12 M18 6 6 18" /> : <path d="M3 6h18 M3 12h18 M3 18h18" />}
            </svg>
          </button>
        </div>
        {menuOpen && (
          <nav className="mkt-mobilenav" onClick={() => setMenuOpen(false)}>
            <Link to={GUIDE_PATHS[lang]}>{t('marketing.nav.features')}</Link>
            <a href="#how">{t('marketing.nav.how')}</a>
            <a href="#pricing">{t('marketing.nav.pricing')}</a>
            <a href="#faq">{t('marketing.nav.faq')}</a>
            <Link to={PARTNER_PATHS[lang]}>{t('partners.nav_label')}</Link>
            <Link to="/login">{t('marketing.nav.login')}</Link>
          </nav>
        )}
      </header>

      <section className="mkt-hero">
        <div className="mkt-hero__text">
          <span className="mkt-badge">{t('marketing.hero_badge')}</span>
          <h1 className="mkt-hero__title">{t('marketing.hero_title')}</h1>
          <p className="mkt-hero__subtitle">{t('marketing.hero_subtitle')}</p>
          <div className="mkt-hero__cta">
            <a href="#demo" className="btn btn--primary" onClick={() => track('demo_click', { from: 'hero' })}>{t('demo.title')}</a>
            <a href="#how" className="btn btn--ghost">{t('marketing.hero_cta2')}</a>
          </div>
          <p className="mkt-trust">{t('marketing.trust')}</p>
        </div>
        <div className="mkt-hero__visual"><PhoneMenu /></div>
      </section>

      <section id="features" className="mkt-section">
        <h2 className="mkt-section__title">{t('marketing.features_title')}</h2>
        <div className="mkt-grid">
          {features.map((f, i) => (
            <div key={i} className="mkt-card">
              <span className="mkt-card__icon">{ICONS[i]}</span>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mkt-section mkt-showcase">
        <div className="mkt-shot">
          <KdsBoard />
          <p>{features[3]?.title}</p>
        </div>
        <div className="mkt-shot">
          <LoyaltyCard />
          <p>{features[4]?.title}</p>
        </div>
      </section>

      <section id="how" className="mkt-section mkt-section--alt">
        <h2 className="mkt-section__title">{t('marketing.how_title')}</h2>
        <div className="mkt-steps">
          {steps.map((s, i) => (
            <div key={i} className="mkt-step">
              <span className="mkt-step__num">{i + 1}</span>
              <div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mkt-section mkt-loyalty">
        <div>
          <h2 className="mkt-section__title" style={{ textAlign: 'left' }}>{t('marketing.loyalty_title')}</h2>
          <p>{t('marketing.loyalty_desc')}</p>
        </div>
        <div className="mkt-loyalty__badge">★</div>
      </section>

      <section id="pricing" className="mkt-section mkt-section--alt">
        <h2 className="mkt-section__title">{t('marketing.pricing_title')}</h2>
        <p className="mkt-section__subtitle">{t('marketing.pricing_subtitle')}</p>
        <div className="mkt-pricing">
          <div className="mkt-plan">
            <h3>{t('marketing.plan_name')}</h3>
            <div className="mkt-plan__price">
              {t('marketing.plan_price')}<span>{t('marketing.plan_period')}</span>
            </div>
            <ul>
              {planFeatures.map((f, i) => <li key={i}>{f}</li>)}
            </ul>
            <a href="#demo" className="btn btn--primary btn--block" onClick={() => track('demo_click', { from: 'pricing' })}>{t('demo.title')}</a>
            <p className="mkt-trial">{t('marketing.trial_note')}</p>
          </div>
          <div className="mkt-addon">
            <h3>{t('marketing.addon_name')}</h3>
            <div className="mkt-addon__price">{t('marketing.addon_price')}</div>
            <p>{t('marketing.addon_desc')}</p>
          </div>
        </div>
      </section>

      <section id="faq" className="mkt-section">
        <h2 className="mkt-section__title">{t('marketing.faq_title')}</h2>
        <div className="mkt-faq">
          {faq.map((item, i) => (
            <details key={i} className="mkt-faq__item" onToggle={(e) => { if ((e.currentTarget as HTMLDetailsElement).open) track('faq_open', { q: item.q }); }}>
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="mkt-cta">
        <h2>{t('marketing.cta_title')}</h2>
        <p>{t('marketing.cta_subtitle')}</p>
        <a href="#demo" className="btn btn--primary" onClick={() => track('demo_click', { from: 'cta' })}>{t('demo.title')}</a>
      </section>

      <DemoForm lang={lang} />

      <footer className="mkt-footer">
        <div className="brand">
          <BrandMark className="brand__mark" />
          <span className="brand__name">La <em>petite</em> <span className="brand__accent">table</span></span>
        </div>
        <p>{t('marketing.footer_tagline')}</p>
        <div className="mkt-footer__links">
          <Link to={GUIDE_PATHS[lang]}>{t('marketing.nav.guide')}</Link>
          <Link to={PARTNER_PATHS[lang]}>{t('partners.nav_label')}</Link>
          <Link to="/termos">{t('legal.terms')}</Link>
          <Link to="/privacidade">{t('legal.privacy')}</Link>
          {LANGS.map((l) => (
            <Link key={l.code} to={LOCALE_PATHS[l.code]}>{l.short}</Link>
          ))}
        </div>
        <p className="mkt-footer__rights">© {t('app.name')} · {t('marketing.footer_rights')}</p>
      </footer>

      <ChatWidget lang={lang} />
      <ConsentBanner />
    </div>
  );
}
